import { assertEuro, materialNetUnitCost } from './quoteFinancialInput';

const valueOf = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return '';
};

const decimal = (value, fallback = '0') => {
  const normalized = String(value ?? '').trim().replace(',', '.');
  return normalized === '' || Number.isNaN(Number(normalized)) ? fallback : normalized;
};

export const normalizeQuoteCatalogProduct = (product = {}) => ({
  ...(product.purchaseCostKnown === undefined ? {} : { purchaseCostKnown: product.purchaseCostKnown }),
  id: valueOf(product, 'id'),
  manufacturerProductId: valueOf(product, 'manufacturerProductId', 'manufacturer_product_id', 'productId', 'product_id'),
  manufacturerName: valueOf(product, 'manufacturerName', 'manufacturer_name')
    || product.manufacturer?.name
    || '',
  reference: valueOf(product, 'reference'),
  name: valueOf(product, 'name'),
  description: valueOf(product, 'description'),
  productFamily: valueOf(product, 'productFamily', 'product_family'),
  unit: valueOf(product, 'unit') || 'U',
  supplierPartnerId: valueOf(product, 'supplierPartnerId', 'supplier_partner_id'),
  supplierName: valueOf(product, 'supplierName', 'supplier_name')
    || product.supplier?.socialReason
    || product.supplier?.legalName
    || product.supplier?.name
    || '',
  grossUnitPurchasePrice: decimal(valueOf(
    product,
    'grossUnitPurchasePrice',
    'gross_unit_purchase_price',
    'catalogUnitPurchasePrice',
    'catalog_unit_purchase_price',
    'unitPurchasePrice',
    'unit_purchase_price',
  )),
  discountRate: decimal(valueOf(product, 'discountRate', 'discount_rate')),
  netUnitPurchasePrice: product.netUnitPurchasePrice !== undefined ? product.netUnitPurchasePrice : product.net_unit_purchase_price,
  currencyCode: valueOf(product, 'currencyCode', 'currency_code') || 'EUR',
  priceSource: valueOf(product, 'priceSource', 'price_source') || 'catalog',
  priceUpdatedAt: valueOf(product, 'priceUpdatedAt', 'price_updated_at'),
  priceValidUntil: valueOf(product, 'priceValidUntil', 'price_valid_until'),
  datasheetUrl: valueOf(product, 'datasheetUrl', 'datasheet_url'),
});

export const quoteCatalogProductLabel = (product = {}) => {
  const normalized = normalizeQuoteCatalogProduct(product);
  return [normalized.manufacturerName, normalized.reference, normalized.name]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' - ');
};

export const materialItemFromCatalogProduct = (product = {}, overrides = {}) => {
  const normalized = normalizeQuoteCatalogProduct(product);
  const gross = Number(normalized.grossUnitPurchasePrice || 0);
  const discount = Math.min(100, Math.max(0, Number(normalized.discountRate || 0)));
  assertEuro(normalized.currencyCode);
  const net = materialNetUnitCost({ ...normalized, catalogUnitPurchasePrice: normalized.grossUnitPurchasePrice });

  return {
    id: `material-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    catalogProductId: normalized.id || null,
    manufacturerProductId: normalized.manufacturerProductId || null,
    manufacturerNameSnapshot: normalized.manufacturerName,
    referenceSnapshot: normalized.reference,
    descriptionSnapshot: normalized.name || normalized.description || normalized.reference || 'Matériel',
    productFamilySnapshot: normalized.productFamily,
    supplierPartnerId: normalized.supplierPartnerId || null,
    supplierNameSnapshot: normalized.supplierName,
    quantity: 1,
    unit: normalized.unit || 'U',
    catalogUnitPurchasePrice: gross,
    unitPurchasePrice: gross,
    discountRate: discount,
    purchasePriceMode: 'catalog',
    discountMode: 'catalog',
    netUnitPurchasePrice: net,
    totalPurchaseCost: net,
    totalEffectiveCost: net,
    currencyCodeSnapshot: normalized.currencyCode,
    priceSourceSnapshot: normalized.priceSource,
    priceUpdatedAtSnapshot: normalized.priceUpdatedAt || null,
    priceValidUntilSnapshot: normalized.priceValidUntil || null,
    datasheetUrlSnapshot: normalized.datasheetUrl || '',
    ...overrides,
  };
};

export const isCatalogPriceExpired = (item = {}, now = new Date()) => {
  if (!item.priceValidUntilSnapshot) return false;
  const end = new Date(item.priceValidUntilSnapshot);
  return !Number.isNaN(end.getTime()) && end.getTime() < now.getTime();
};
